import  {  useState } from 'react';
import { useAddRateMutation, useAddLikeMutation, useDeleteRoomLikeMutation } from '../redux/services/rooms/roomAPI';
import { Link } from 'react-router-dom';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import queryString from 'query-string';
import Swal from 'sweetalert2';
import Heart from "react-animated-heart";

const getRoomRate = (roomRate, roomId) => {
  if (!roomRate?.length) return '0.0';
  const matches = roomRate.filter((rate) => Number(rate.room) === Number(roomId));
  if (!matches.length) return '0.0';
  const average = matches.reduce((sum, rate) => sum + Number(rate.score || 0), 0) / matches.length;
  return average.toFixed(1);
};

const Room = ({ recordsbool, records, user, data, children, sliceFirstThree }) => {
  const [addRate, { error: rateError, isError: rateIsError }] = useAddRateMutation();
  const [deleteRoomLike] = useDeleteRoomLikeMutation();
  const [addLike, { error: likeError, isError: likeIsError }] = useAddLikeMutation();
  const [removedRoomIds, setRemovedRoomIds] = useState([]);
  const [optimisticLikes, setOptimisticLikes] = useState(data?.ser_room_likes || []);

  const rooms = data;
  const allRooms = rooms?.ser_data || [];
  const roomRate = rooms?.ser_room_rating || [];
  const roomLikes = optimisticLikes;
  const visibleRooms = recordsbool ? (records || []) : (sliceFirstThree ? allRooms.slice(0, 3) : allRooms);

  const handleRating = async (newValue, roomId) => {
    if (!user) {
      Swal.fire({
        icon: 'info',
        html: 'Please <a href="/login">login</a> to rate rooms.',
        confirmButtonText: 'Got it',
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
      return;
    }

    if (rateIsError) {
      Swal.fire({
        icon: 'error',
        text: rateError?.data || 'Rating failed.',
        confirmButtonText: 'Try again',
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
      return;
    }

    const response = await addRate(queryString.stringify({
      score: newValue,
      room: roomId,
      user_liked: user?.id,
    }));

    if (response?.data) {
      Swal.fire({
        icon: 'success',
        text: response.data.message,
        timer: 1600,
        showConfirmButton: false,
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
    } else {
      Swal.fire({
        icon: 'warning',
        text: response?.error?.data?.message || 'Unable to submit rating.',
        confirmButtonText: 'OK',
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
    }
  };

  const handleLike = async (roomId) => {
    if (!user) {
      Swal.fire({
        icon: 'info',
        html: 'Please <a href="/login">login</a> to save favorites.',
        confirmButtonText: 'Got it',
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
      return;
    }

    if (likeIsError) {
      Swal.fire({
        icon: 'error',
        text: likeError?.data || 'Favorite action failed.',
        confirmButtonText: 'Try again',
        customClass: { popup: 'sweetalert2-custom-popup' },
      });
      return;
    }

    const like = roomLikes?.find((item) => Number(item?.user_liked) === Number(user?.id) && Number(item?.room) === Number(roomId));

    if (like) {
      await deleteRoomLike(like.id);
      setOptimisticLikes((current) => current.filter((item) => item.id !== like.id));
      setRemovedRoomIds((current) => [...current, roomId]);
      Swal.fire({
        icon: 'success',
        title: 'Removed from favourites',
        timer: 1200,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        customClass: { popup: 'sweetalert2-toast-custom' },
      });
    } else {
      const response = await addLike(queryString.stringify({
        room: parseInt(roomId, 10),
        user_liked: user?.id,
      }));

      if (response?.data) {
        setOptimisticLikes((current) => [...current, { room: roomId, user_liked: user.id }]);
        Swal.fire({
          icon: 'success',
          title: response.data.message,
          width: 400,
          timer: 1600,
          showConfirmButton: false,
          customClass: { popup: 'sweetalert2-custom-popup' },
        });
      } else {
        Swal.fire({
          icon: 'warning',
          text: response?.error?.data?.message || 'Unable to save favorite.',
          confirmButtonText: 'OK',
          customClass: { popup: 'sweetalert2-custom-popup' },
        });
      }
    }
  };

  const renderRoom = (room) => {
    if (!room) return null;
    const liked = roomLikes?.some((like) => Number(like?.user_liked) === Number(user?.id) && Number(like?.room) === Number(room.id));
    const rating = getRoomRate(roomRate, room.id);
    const roomType = room.room_styles?.room_type || 'Premium stay';
    const location = room.location || room.city || roomType;
    const isRemoved = removedRoomIds.includes(room.id);

    return (
      <article key={room.id} className={`premium-card ${isRemoved ? 'opacity-0' : 'opacity-100'}`}>
        <Link to={`/room/${encodeURIComponent(room.room_name)}`} state={{ data: room }}>
          <div className="relative h-64 overflow-hidden bg-slate-100 sm:h-72">
            <img className="h-full w-full object-cover transition duration-500 group-hover:scale-105" src={room.main_img} alt={room.room_name} loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent" />
            <div className="absolute left-4 top-4 flex gap-2">
              <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-black uppercase tracking-wide text-ink shadow-sm">Available</span>
              <span className="rounded-full bg-gotrip-700/90 px-3 py-1 text-xs font-bold text-white shadow-sm">{room.beds} beds</span>
            </div>
            {user && (
              <button
                type="button"
                className="room-favorite-btn"
                onClick={(event) => {
                  event.preventDefault();
                  handleLike(room.id);
                }}
                aria-label={liked ? "Remove from favorites" : "Add to favorites"}
              >
                <Heart isClick={liked} />
              </button>
            )}
          </div>
        </Link>

        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-gotrip-700">{location}</p>
              <Link to={`/room/${encodeURIComponent(room.room_name)}`} state={{ data: room }} className="mt-1 block text-xl font-black tracking-tight text-ink hover:text-gotrip-700">
                {room.room_name}
              </Link>
            </div>
            <div className="rounded-2xl bg-amber-50 px-3 py-2 text-right">
              <p className="text-xs font-black uppercase tracking-wide text-amber-600">Rating</p>
              <p className="text-lg font-black text-amber-600">{rating}</p>
            </div>
          </div>

          {user && (
            <div className="mt-4 flex items-center gap-3">
              <Stack spacing={1}>
                <Rating
                  value={roomRate?.find((rate) => Number(rate.user_liked) === Number(user?.id) && Number(rate.room) === Number(room.id))?.score || 0}
                  onChange={(_, newValue) => handleRating(newValue, room.id)}
                  precision={0.5}
                  size="small"
                />
              </Stack>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-bold text-muted">{rating}/5</span>
            </div>
          )}

          <div className="mt-4 rounded-2xl bg-gotrip-50 p-4">
            <p className="text-xs font-black uppercase tracking-wide text-gotrip-700">Product Transformation</p>
            <p className="mt-2 text-sm leading-6 text-muted">Room details, ratings, pricing, and booking actions are grouped into one clear decision card.</p>
          </div>

          <div className="mt-5 flex items-center justify-between gap-4 border-t border-line pt-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-muted">From</p>
              <p className="text-2xl font-black text-ink">${room.price}</p>
            </div>
            <Link to={`/room/${encodeURIComponent(room.room_name)}`} state={{ data: room }} className="btn-primary">
              Review details
            </Link>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section className="bg-white py-20">
      <div className="section-shell">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          {children}
        </div>

        {visibleRooms.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visibleRooms.map(renderRoom)}
          </div>
        ) : (
          <div className="premium-card p-10 text-center">
            <p className="text-lg font-bold text-muted">No rooms match your current search.</p>
            <Link to="/rooms" className="btn-secondary mt-5">Reset filters</Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Room;
